import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Field, Form } from 'react-final-form';

import { useConfirm } from 'material-ui-confirm';
import { Button, TextField, Typography } from '@mui/material';
import Grid from 'material/components/Grid';
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';

import { Callout } from 'common/components';
import {
  deleteEmailDomain,
  fetchEmailDomains,
  postEmailDomains,
} from 'store/actions/organizations';
import { organizationSelectors } from 'store/selectors/organizations';
import { setSnackbarSuccess } from 'store/actions/global';
import { domainEmailFormFields } from 'utils/constants';
import { useCopyToClipboard } from 'utils/hooks';
import { Bold, FormWrapper, Mono, SettingsWrapper, StyledHighlightOffIcon } from 'globalStyles';

const RemoveButton = styled(Button)``;

const StyledGrid = styled(Grid)`
  margin-top: 20px;
`;

const StyledField = styled(Field)`
  padding-top: 20px;
`;

const DNSWrapper = styled.div`
  padding-bottom: 20px;

  & > div {
    display: flex;
    justify-content: flex-end;
  }
`;

const DNSDataList = styled.ul`
  margin: 0;
  padding-top: 20px;
`;

const DNSDataPoint = styled.li`
  display: flex;
  align-items: center;
  padding-bottom: 4px;
  list-style-type: none;
  color: ${({ theme }) => theme.colors.osloGray};
`;

const DomainInfo = styled.div`
  margin: 30px 0;
  color: ${({ theme }) => theme.colors.osloGray};

  & > span {
    display: inline-block;
    margin: 0 50px 0 30px;
    color: #717d85;
    font-weight: 500;
  }
`;

const ADD_BUTTON_TEXT = 'Add';
const DELETE_BUTTON_TEXT = 'Delete';
const YOUR_DOMAIN_TITLE = 'Send email from your domain name';
const EMAIL_DOMAIN_TITLE = 'Sending Domain';
const HELP_CENTER_ARTICLE =
  'https://help.muster.com/en/articles/7170321-authenticating-your-email-sending-domain';

const DomainTab = () => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const { organizationUuid } = useParams();
  const [copy] = useCopyToClipboard();

  const { emailDomainData } = useSelector(state => ({
    emailDomainData: organizationSelectors.getEmailDomainData(state),
  }));

  useEffect(() => {
    dispatch(fetchEmailDomains(organizationUuid));
  }, []);

  const handleDeleteDomain = uuid => {
    confirm({
      confirmationText: 'Delete',
      description:
        'This will remove the domain from your account and disable DKIM email authentication from Muster',
      title: 'Delete sending domain',
    })
      .then(() => dispatch(deleteEmailDomain(uuid)))
      .then(() => dispatch(fetchEmailDomains(organizationUuid)));
  };

  const handleDomainSubmit = async values => {
    const params = {
      ...values,
      organization: organizationUuid,
    };

    try {
      await dispatch(postEmailDomains(params));
      dispatch(fetchEmailDomains(organizationUuid));
      dispatch(setSnackbarSuccess({ message: 'Correctly added Domain' }));

      return 'success';
    } catch (error) {
      return error;
    }
  };

  const domain = emailDomainData[0];
  const sendingDomain = domain?.domain;
  const hasDomain = !!domain?.domain;
  const dnsConfiguration = domain?.dns;
  const spfValid = domain?.spfValid;
  const spfDomainFound = domain?.spfDomainFound;
  const spfDomainTarget = domain?.spfDomainTarget;
  const zoneFileUrl = domain?.uuid
    ? `${process.env.REACT_APP_BACKEND_URL}/v1/accounts/email-domains/${domain.uuid}/zone/`
    : null;

  return (
    <SettingsWrapper>
      <FormWrapper>
        {hasDomain ? (
          <>
            <Typography variant="h6">{YOUR_DOMAIN_TITLE}</Typography>
            <DomainInfo>
              Your domain name:
              <span>{domain?.domain}</span>
              <RemoveButton
                color="primary"
                endIcon={<DeleteOutlineIcon />}
                onClick={() => handleDeleteDomain(domain.uuid)}
                type="button"
                variant="text"
              >
                {DELETE_BUTTON_TEXT}
              </RemoveButton>
            </DomainInfo>
          </>
        ) : (
          <>
            <Typography variant="h6">{EMAIL_DOMAIN_TITLE}</Typography>
            <div>
              <p>
                Your sending domain is the domain in your email address. E.g. if your email address
                is <strong>john.doe@example.com</strong> then your sending domain is{' '}
                <strong>example.com</strong>
              </p>
              <p>
                Muster will generate custom DNS records you can use to authenticate your domain for
                sending email from Muster. See our{' '}
                <a href={HELP_CENTER_ARTICLE} target="_blank" rel="noreferrer">
                  detailed help article
                </a>{' '}
                for complete instructions.
              </p>
            </div>
            <Form
              initialValues={domain}
              onSubmit={handleDomainSubmit}
              render={({ handleSubmit, pristine, submitting }) => (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    {domainEmailFormFields.map(({ label, name, type }) => (
                      <Grid alignContent="space-between" key={label} size={9}>
                        <StyledField name={name}>
                          {({ input, meta }) => (
                            <TextField
                              {...input}
                              disabled={hasDomain}
                              error={Boolean(meta.touched && (meta.error || meta.submitError))}
                              fullWidth
                              helperText={meta.touched && (meta.error || meta.submitError)}
                              label={label}
                              type={type}
                            />
                          )}
                        </StyledField>
                      </Grid>
                    ))}
                    <StyledGrid justifyContent="flex-end" size={3}>
                      <Button
                        color="secondary"
                        disabled={submitting || pristine}
                        disableElevation
                        type="submit"
                        variant="contained"
                      >
                        {ADD_BUTTON_TEXT}
                      </Button>
                    </StyledGrid>
                  </Grid>
                </form>
              )}
            />
          </>
        )}
        {dnsConfiguration && (
          <>
            <p>
              Use the DNS records below to authenticate your domain for sending email from Muster.
              See our{' '}
              <a href={HELP_CENTER_ARTICLE} target="_blank" rel="noreferrer">
                detailed help article
              </a>{' '}
              for complete instructions. If you need to make changes to the domain, delete it and
              then add a replacement.
            </p>
            <Callout title="Is someone else updating your domain?">
              <p>
                If you need to provide this information to someone else providing your team with IT
                support, you can &nbsp;
                <a href={zoneFileUrl}>download this partial zone file 💾</a>
                &nbsp; and share it with them.
              </p>
            </Callout>
            <h3>DKIM Configuration</h3>
            {dnsConfiguration?.map(({ label, name, valid, value }) => (
              <DNSWrapper key={`${value}-${name}`}>
                <DNSDataList key={label}>
                  <DNSDataPoint>
                    Host name: &nbsp;
                    <Mono>{name}</Mono>
                  </DNSDataPoint>
                  <DNSDataPoint>
                    Record type: &nbsp;
                    <Mono>CNAME</Mono>
                  </DNSDataPoint>
                  <DNSDataPoint>
                    Value: &nbsp;
                    <Mono>{value}</Mono>
                  </DNSDataPoint>
                  <DNSDataPoint>
                    {'Valid: '}
                    {valid ? (
                      <CheckCircleOutlineIcon color="secondary" />
                    ) : (
                      <StyledHighlightOffIcon />
                    )}
                  </DNSDataPoint>
                </DNSDataList>
                <div>
                  <Button
                    color="primary"
                    endIcon={<DeleteOutlineIcon />}
                    onClick={() => copy(`${name} CNAME ${value}`)}
                    type="button"
                    variant="text"
                  >
                    Copy
                  </Button>
                </div>
              </DNSWrapper>
            ))}
            <Callout severity="warning">
              The records above include the full domain name, including the root domain. Many DNS
              providers will require only the subdomain portion of the name, e.g.{' '}
              <Mono>dkim._domainkey</Mono> and not <Mono>{`dkim._domainkey.${domain.domain}`}</Mono>
              .
            </Callout>
            <h3>SPF Configuration</h3>
            <DNSDataList key="spf">
              <DNSDataPoint>
                Host name: &nbsp;
                <Mono>{sendingDomain}</Mono>
              </DNSDataPoint>
              <DNSDataPoint>
                Record type: &nbsp;
                <Mono>TXT</Mono>
              </DNSDataPoint>
              <DNSDataPoint>
                Found value: &nbsp;
                <Mono>{spfDomainFound}</Mono>
              </DNSDataPoint>
              <DNSDataPoint>
                Target value: &nbsp;
                <Mono>{spfValid ? spfDomainFound : spfDomainTarget}</Mono>
              </DNSDataPoint>
              <DNSDataPoint>
                {'Valid: '}
                {spfValid ? (
                  <CheckCircleOutlineIcon color="secondary" />
                ) : (
                  <StyledHighlightOffIcon />
                )}
              </DNSDataPoint>
            </DNSDataList>
            {!spfValid && (
              <>
                <p>
                  Please note, if you just added your sending domain, the initial verification check
                  could still be pending.
                </p>
                <p>
                  Otherwise, ensure your existing SPF record includes the stanza{' '}
                  <Mono>
                    <Bold>include:_spf.muster.com</Bold>
                  </Mono>
                </p>
              </>
            )}
          </>
        )}
      </FormWrapper>
    </SettingsWrapper>
  );
};

DomainTab.propTypes = {};

export default DomainTab;
